import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { GetSocialAnalyticsPostsWithInsightsQuery, SocialPostType } from '@frontend/gql/social/graphql';

import { Card, CardContent, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Skeleton } from '@/shadcn/components/ui/skeleton';

import { POST_TYPE_ORDER, getPostTypeColor, getPostTypeDisplayName } from '../../constants/postTypes';

interface PostsBreakdownByNetworkTotalCardProps {
  data: GetSocialAnalyticsPostsWithInsightsQuery['client']['socialAnalytics']['postsBreakdown'] | undefined;
  fetching: boolean;
}

interface ChartDataPoint {
  postType: SocialPostType;
  displayName: string;
  activePosts: number;
  fill: string;
}

const PostsBreakdownByNetworkTotalCard: React.FC<PostsBreakdownByNetworkTotalCardProps> = ({ data, fetching }) => {
  const chartData = useMemo(() => {
    if (!data?.byPostType?.total?.length) return [];

    // Aggregate by post type across all networks
    const postTypeAggregates: Record<SocialPostType, number> = {} as Record<SocialPostType, number>;

    data.byPostType.total.forEach((item) => {
      const postType = item.postType as SocialPostType;
      postTypeAggregates[postType] = (postTypeAggregates[postType] || 0) + (item.activePosts || 0);
    });

    // Create chart data only for post types present in the dataset (including 0 values)
    const result: ChartDataPoint[] = POST_TYPE_ORDER.filter((postType) => postType in postTypeAggregates).map(
      (postType) => ({
        postType,
        displayName: getPostTypeDisplayName(postType),
        activePosts: postTypeAggregates[postType] || 0,
        fill: getPostTypeColor(postType),
      }),
    );

    return result;
  }, [data]);

  if (fetching) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Posts Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Posts Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">No data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Posts Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="displayName"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12 }}
                width={100}
              />
              <Tooltip
                formatter={(value: number) => [value, 'Posts']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                }}
              />
              <Bar dataKey="activePosts" radius={[0, 4, 4, 0]} maxBarSize={40}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default PostsBreakdownByNetworkTotalCard;
